import React, { useCallback, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { PrimaryButton, SecondaryButton, ease, text } from '../styles/GlobalStyles';
import { ArrowRightIcon } from './common/Icons';

// Hero photo: AI-generated for this site, so it needs no attribution.
const PHOTO = {
  small: '/images/desk-1600.jpg',
  large: '/images/desk-2000.jpg',
  // Where the subject sits in the frame, and how far down (% of its height) it starts so the peeking card shows the lock screen.
  position: '50% 50%',
  startShift: 30,
};

// Pinned for ~1.6 screens: the headline lifts away while the photo opens from a card to full bleed.
const HeroSection = styled.section<{ $pinned: boolean }>`
  position: relative;
  height: ${props => (props.$pinned ? '260vh' : '100svh')};
`;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100svh;
  overflow: hidden;
`;

const Intro = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: calc(60px + 8vh) 24px 0;
  text-align: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-top: calc(60px + 6vh);
  }
`;

const Byline = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  font-size: ${text.sm};
  color: ${props => props.theme.colors.textSecondary};

  img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
  }

  b {
    font-weight: 500;
    color: ${props => props.theme.colors.text};
  }
`;

const Name = styled(motion.h1)`
  font-size: clamp(3rem, 9vw, 7.5rem);
  font-weight: 600;
  line-height: 0.95;
  letter-spacing: -0.05em;
  color: ${props => props.theme.colors.text};
  text-wrap: balance;
`;

const Sub = styled(motion.p)`
  max-width: 34ch;
  margin-top: 20px;
  font-size: ${text.lg};
  line-height: 1.45;
  color: ${props => props.theme.colors.textSecondary};
  text-wrap: balance;
`;

const Actions = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
`;

// Extends 12% below the viewport so the card's bottom corners stay off-screen while it peeks up.
const Media = styled(motion.div)`
  position: absolute;
  inset: 0 0 -12% 0;
  z-index: 1;
  overflow: hidden;
  background: #0d0d0f;
`;

const Photo = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: ${PHOTO.position};
`;

const Shade = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.66) 100%);
`;

const Caption = styled(motion.p)`
  position: absolute;
  left: max(32px, calc((100vw - 1160px) / 2 + 32px));
  right: 32px;
  bottom: calc(12% + 9vh);
  max-width: 16ch;
  font-size: clamp(2.2rem, 5.5vw, 4.5rem);
  font-weight: 600;
  line-height: 1.02;
  letter-spacing: -0.04em;
  color: #fff;
  text-wrap: balance;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    left: 20px;
    right: 20px;
  }
`;

const mix = (from: number, to: number, amount: number) => from + (to - from) * amount;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: ease.out } },
};

const Hero: React.FC = () => {
  const { content } = useAppContext();
  const { personalInfo, hero } = content;
  const reduceMotion = useReducedMotion();
  const pinned = !reduceMotion;

  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] });
  const smooth = useSpring(scrollYProgress, { stiffness: 220, damping: 40, restDelta: 0.0005 });
  const still = useMotionValue(0);
  const progress = pinned ? smooth : still;

  const introOpacity = useTransform(progress, [0, 0.28], [1, 0]);
  const introY = useTransform(progress, [0, 0.4], ['0vh', '-14vh']);
  const introScale = useTransform(progress, [0, 0.4], [1, 0.94]);
  const photoScale = useTransform(progress, [0, 0.7], [1.12, 1]);
  const captionOpacity = useTransform(progress, [0.55, 0.75], [0, 1]);
  // Darkening is only for the caption's legibility, so it arrives with the caption.
  const shadeOpacity = useTransform(progress, [0.4, 0.7], [0, 1]);
  const captionY = useTransform(progress, [0.55, 0.75], [32, 0]);

  // The card's top edge rests just under the buttons on any screen; its sides inset by `side` %.
  // Measured into a ref and applied directly, so a late measurement can never be missed.
  const actionsRef = useRef<HTMLDivElement>(null);
  const geometry = useRef({ top: 57, side: 13 });
  const clipPath = useMotionValue('inset(57% 13% 0% 13% round 28px)');
  // The photo starts lower so the iPhone fills the peeking card, and rises on the card's own curve so its top edge never shows.
  const photoY = useMotionValue(`${PHOTO.startShift}%`);

  const applyClip = useCallback(() => {
    const amount = easeOut(Math.min(1, Math.max(0, progress.get() / 0.55)));
    const { top, side } = geometry.current;
    const bottom = (12 / 112) * 100;
    clipPath.set(`inset(${mix(top, 0, amount)}% ${mix(side, 0, amount)}% ${mix(0, bottom, amount)}% ${mix(side, 0, amount)}% round ${mix(28, 0, amount)}px)`);
    photoY.set(`${mix(PHOTO.startShift, 0, amount)}%`);
  }, [progress, clipPath, photoY]);

  useMotionValueEvent(progress, 'change', applyClip);

  useLayoutEffect(() => {
    const measure = () => {
      const viewport = window.innerHeight;
      const actions = actionsRef.current;
      // offsetTop ignores the intro's scroll transform, so this is the buttons' resting position.
      const actionsBottom = actions ? actions.offsetTop + actions.offsetHeight : viewport * 0.6;
      const cardTop = Math.min(actionsBottom + 44, viewport * 0.8);
      geometry.current = {
        top: (cardTop / (viewport * 1.12)) * 100,
        side: window.innerWidth < 768 ? 5 : 13,
      };
      applyClip();
    };
    measure();
    document.fonts?.ready.then(measure);
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [applyClip]);

  const city = personalInfo.location.split(',').pop()?.replace(' / ', ', ').trim();
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <HeroSection id="home" ref={sectionRef} $pinned={pinned}>
      <Sticky>
        <Media style={{ clipPath }} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25, ease: ease.out }}>
          <Photo src={PHOTO.small} srcSet={`${PHOTO.small} 1600w, ${PHOTO.large} 2000w`} sizes="100vw" alt="" style={{ scale: photoScale, y: photoY }} />
          <Shade style={{ opacity: shadeOpacity }} />
          <Caption style={{ opacity: captionOpacity, y: captionY }}>{hero.features[0]}</Caption>
        </Media>

        <Intro style={{ opacity: introOpacity, y: introY, scale: introScale }}>
          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } } }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <Byline variants={item}>
              <img src={personalInfo.profileImage} alt="" />
              <span>
                <b>{personalInfo.title}</b> — {city}
              </span>
            </Byline>
            <Name variants={item}>{personalInfo.name}</Name>
            <Sub variants={item}>{hero.features[1]}</Sub>
            <Actions variants={item} ref={actionsRef}>
              <PrimaryButton onClick={() => scrollTo('projects')}>
                {hero.projectsButton}
                <ArrowRightIcon size={15} />
              </PrimaryButton>
              <SecondaryButton onClick={() => scrollTo('about')}>{hero.aboutButton}</SecondaryButton>
            </Actions>
          </motion.div>
        </Intro>
      </Sticky>
    </HeroSection>
  );
};

export default Hero;
